import { useEffect, useMemo, useRef, useState, memo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Check,
  Download,
  Save,
  Video,
  ChevronDown,
  Keyboard,
  Settings,
  Sun,
  Moon,
  ChevronUp,
  Wand2,
  Film,
  RefreshCw,
  ScissorsLineDashed,
} from 'lucide-react';
import { useEditorStore } from '../stores/editor-store';
import { useSettingsStore } from '@/features/settings/stores/settings-store';
import { UnsavedChangesDialog } from './unsaved-changes-dialog';
import { ShortcutsDialog } from './shortcuts-dialog';
import { SettingsDialog } from './settings-dialog';
import { useTranslation } from '@/features/i18n/locales';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import { useItemsStore } from '@/features/timeline/stores/items-store';
import { updateProject } from '@/lib/storage/indexeddb';
import { useProjectStore } from '@/features/projects/stores/project-store';
import {
  hasInjectedCta,
  removeCtaFromTimeline,
  upsertCtaIntoTimeline,
} from '@/utils/cta-insertion';
import { insertSavedSceneAsset } from '../utils/scene-asset-insertion';

interface ToolbarProps {
  projectId: string;
  project: {
    id: string;
    name: string;
    width: number;
    height: number;
    fps: number;
    brandProfileId?: string;
    ctaConfig?: any;
    input_data?: any;
  };
  isDirty?: boolean;
  onSave?: () => Promise<void>;
  onExport?: () => void;
}

export const Toolbar = memo(function Toolbar({ project, isDirty = false, onSave, onExport }: ToolbarProps) {
  const navigate = useNavigate();
  const t = useTranslation();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const currentProject = useProjectStore((s) => s.currentProject);
  const autoTrimVoiceoverSilence = useSettingsStore((s) => s.autoTrimVoiceoverSilence);
  const setSetting = useSettingsStore((s) => s.setSetting);
  const fps = useTimelineStore((s) => s.fps);
  const effectiveProject = currentProject?.id === project.id ? currentProject : null;
  const resolvedCtaConfig =
    effectiveProject?.ctaConfig ||
    effectiveProject?.input_data?.ctaConfig ||
    project.ctaConfig ||
    project.input_data?.ctaConfig;
  const [ctaConfig, setCtaConfig] = useState<any>(resolvedCtaConfig);
  const items = useTimelineStore((s) => s.items);
  const hasCtaInTimeline = useMemo(() => hasInjectedCta(items), [items]);
  const applyBranding = ctaConfig?.applyBranding ?? true;
  const showLogo = ctaConfig?.showLogo ?? true;
  const availableBrandProfiles =
    effectiveProject?.input_data?.availableBrandProfiles ||
    project.input_data?.availableBrandProfiles ||
    [];
  const availableCtaPresets =
    effectiveProject?.input_data?.availableCtaPresets ||
    project.input_data?.availableCtaPresets ||
    [];
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [selectedPlacement, setSelectedPlacement] = useState<string>('end');
  const [selectedSceneId, setSelectedSceneId] = useState<string>('');
  const [savedSceneAssets, setSavedSceneAssets] = useState<any[]>([]);
  const [publicSceneTemplates, setPublicSceneTemplates] = useState<any[]>([]);
  const [isLoadingSceneAssets, setIsLoadingSceneAssets] = useState(false);
  const [isImportingTemplate, setIsImportingTemplate] = useState(false);
  const [sceneInsertPlacement, setSceneInsertPlacement] = useState<'start' | 'end' | 'after_scene'>('end');
  const [sceneInsertAfterId, setSceneInsertAfterId] = useState<string>('');
  const sceneInsertPlacementRef = useRef(sceneInsertPlacement);
  const sceneInsertAfterIdRef = useRef(sceneInsertAfterId);
  const sceneOptions = useMemo(
    () =>
      items
        .filter((item) => !String(item.originId || '').startsWith('cta-block'))
        .filter((item) => item.label?.startsWith('Scene'))
        .map((item) => ({ id: item.id, label: item.label || `Scene ${item.id}` })),
    [items]
  );

  const isIntegration = /integration=cenema-studio/i.test(window.location.href) ||
    window.location.search.includes('integration') ||
    (window.self !== window.top);
  const handleTrimAllVoiceovers = async () => {
    const { collectVoiceoverTrimUpdates } = await import('@/lib/audio/trim-silence');
    const { trimAudioSilence } = await import('@/features/timeline/stores/actions/item-actions');
    const currentItems = useTimelineStore.getState().items;
    const updates = await collectVoiceoverTrimUpdates(currentItems as any, fps);

    if (updates.length === 0) {
      toast.message('줄일 무음 구간이 없습니다.');
      return;
    }

    trimAudioSilence(updates);
    toast.success('전체 보이스오버 무음을 제거했습니다.');
  };

  useEffect(() => {
    setCtaConfig(
      effectiveProject?.ctaConfig ||
      effectiveProject?.input_data?.ctaConfig ||
      project.ctaConfig ||
      project.input_data?.ctaConfig
    );
  }, [effectiveProject?.ctaConfig, effectiveProject?.input_data?.ctaConfig, project.ctaConfig, project.input_data?.ctaConfig]);

  useEffect(() => {
    const nextConfig =
      effectiveProject?.ctaConfig ||
      effectiveProject?.input_data?.ctaConfig ||
      project.ctaConfig ||
      project.input_data?.ctaConfig;
    setSelectedBrandId(
      nextConfig?.brandSnapshot?.id ||
      availableBrandProfiles.find((brand: any) => brand.isDefault)?.id ||
      ''
    );
    setSelectedPresetId(
      nextConfig?.presetId ||
      nextConfig?.presetSnapshot?.id ||
      availableCtaPresets[0]?.id ||
      ''
    );
    setSelectedPlacement(nextConfig?.placement || 'end');
    setSelectedSceneId(nextConfig?.insertAfterSceneId || '');
  }, [effectiveProject?.ctaConfig, effectiveProject?.input_data?.ctaConfig, project.ctaConfig, project.input_data?.ctaConfig, availableBrandProfiles, availableCtaPresets]);

  useEffect(() => {
    sceneInsertPlacementRef.current = sceneInsertPlacement;
  }, [sceneInsertPlacement]);

  useEffect(() => {
    sceneInsertAfterIdRef.current = sceneInsertAfterId;
  }, [sceneInsertAfterId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SCENE_ASSET_SAVE_RESULT') {
        if (event.data?.payload?.success || event.data?.data?.success) {
          const sceneName = event.data?.payload?.sceneName || event.data?.data?.sceneName || '씬 자산';
          toast.success(`${sceneName} 저장 완료`);
        } else {
          toast.error(event.data?.payload?.message || event.data?.data?.message || '씬 자산 저장 실패');
        }
        return;
      }

      if (event.data?.type === 'SCENE_ASSETS_LIST') {
        const success = event.data?.payload?.success ?? event.data?.data?.success;
        const scenes = event.data?.payload?.scenes || event.data?.data?.scenes || [];
        const templates = event.data?.payload?.templates || event.data?.data?.templates || [];
        setIsLoadingSceneAssets(false);
        if (success) {
          setSavedSceneAssets(scenes);
          setPublicSceneTemplates(templates);
        } else {
          toast.error(event.data?.payload?.message || event.data?.data?.message || '씬 목록을 불러오지 못했습니다.');
        }
        return;
      }

      if (event.data?.type === 'SCENE_TEMPLATE_IMPORT_RESULT') {
        setIsImportingTemplate(false);
        const success = event.data?.payload?.success ?? event.data?.data?.success;
        const scene = event.data?.payload?.scene || event.data?.data?.scene;
        if (!success || !scene) {
          toast.error(event.data?.payload?.message || event.data?.data?.message || '공용 템플릿 가져오기에 실패했습니다.');
          return;
        }

        insertSavedSceneAsset(scene, {
          placement: sceneInsertPlacementRef.current,
          anchorSceneItemId: sceneInsertPlacementRef.current === 'after_scene' ? sceneInsertAfterIdRef.current : undefined,
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const requestSceneAssets = () => {
    if (!isIntegration) return;
    setIsLoadingSceneAssets(true);
    window.parent.postMessage({ type: 'REQUEST_SCENE_ASSETS' }, '*');
  };

  const importPublicTemplate = (templateId: string) => {
    if (!isIntegration) return;
    setIsImportingTemplate(true);
    window.parent.postMessage({
      type: 'IMPORT_SCENE_TEMPLATE',
      payload: { templateId },
      data: { templateId },
    }, '*');
  };

  useEffect(() => {
    if (!sceneInsertAfterId && sceneOptions[0]?.id) {
      setSceneInsertAfterId(sceneOptions[0].id);
    }
  }, [sceneInsertAfterId, sceneOptions]);

  const buildSelectedCtaConfig = () => {
    const selectedBrand = availableBrandProfiles.find((brand: any) => brand.id === selectedBrandId) || undefined;
    const selectedPreset = availableCtaPresets.find((preset: any) => preset.id === selectedPresetId) || undefined;

    if (!selectedPreset && !ctaConfig?.presetSnapshot && !ctaConfig?.overrideHeadline) {
      return undefined;
    }

    return {
      ...ctaConfig,
      presetId: selectedPreset?.id || ctaConfig?.presetId,
      brandSnapshot: selectedBrand || ctaConfig?.brandSnapshot,
      presetSnapshot: selectedPreset || ctaConfig?.presetSnapshot,
      placement: selectedPlacement,
      insertAfterSceneId: selectedPlacement === 'after_scene' ? selectedSceneId : undefined,
      applyBranding,
      showLogo: applyBranding ? showLogo : false,
      showHandle: applyBranding ? (ctaConfig?.showHandle ?? true) : false,
    };
  };

  const handleBackClick = () => {
    if (isIntegration) {
      window.parent.postMessage({ type: 'EXIT_STUDIO' }, '*');
      return;
    }

    if (isDirty) {
      setShowUnsavedDialog(true);
    } else {
      navigate({ to: '/projects' });
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave();
    }
  };

  const persistCtaConfig = async (nextConfig: any) => {
    setCtaConfig(nextConfig);
    await updateProject(project.id, { ctaConfig: nextConfig });
    if (effectiveProject?.id === project.id) {
      useProjectStore.getState().setCurrentProject({
        ...effectiveProject,
        ctaConfig: nextConfig,
      });
    }
  };

  const handleCtaUpsert = async () => {
    const baseConfig = buildSelectedCtaConfig();
    if (!baseConfig) {
      toast.error('삽입할 영상 삽입 문구 설정이 없습니다');
      return;
    }

    const nextConfig = {
      ...baseConfig,
      enabled: true,
      applyBranding,
      showLogo: applyBranding ? showLogo : false,
      showHandle: applyBranding ? (baseConfig?.showHandle ?? true) : false,
    };

    const timelineStore = useTimelineStore.getState();
    const updated = upsertCtaIntoTimeline({
      tracks: [...timelineStore.tracks],
      items: [...timelineStore.items],
      assets: [],
      ctaConfig: nextConfig,
      width: project.width,
      height: project.height,
      fps: project.fps,
    });

    timelineStore.setTracks(updated.tracks);
    useItemsStore.getState().setItems(updated.items);
    timelineStore.markDirty();
    await persistCtaConfig(nextConfig);
    toast.success(hasCtaInTimeline ? 'CTA를 업데이트했습니다' : 'CTA를 삽입했습니다');
  };

  const handleCtaRemove = async () => {
    if (!hasCtaInTimeline) {
      toast.message('삭제할 CTA가 없습니다');
      return;
    }

    const timelineStore = useTimelineStore.getState();
    const cleaned = removeCtaFromTimeline({
      tracks: [...timelineStore.tracks],
      items: [...timelineStore.items],
    });

    timelineStore.setTracks(cleaned.tracks);
    useItemsStore.getState().setItems(cleaned.items);
    timelineStore.markDirty();
    await persistCtaConfig({
      ...ctaConfig,
      enabled: false,
    });
    toast.success('CTA를 제거했습니다');
  };

  const handleToggleBranding = async (checked: boolean) => {
    const baseConfig = buildSelectedCtaConfig() || ctaConfig || {};
    await persistCtaConfig({
      ...baseConfig,
      applyBranding: checked,
      showLogo: checked ? (baseConfig?.showLogo ?? true) : false,
      showHandle: checked ? (baseConfig?.showHandle ?? true) : false,
    });
  };

  const handleToggleLogo = async (checked: boolean) => {
    const baseConfig = buildSelectedCtaConfig() || ctaConfig || {};
    await persistCtaConfig({
      ...baseConfig,
      showLogo: checked,
    });
  };

  const handleSelectBrand = async (brandId: string) => {
    setSelectedBrandId(brandId);
    const selectedBrand = availableBrandProfiles.find((brand: any) => brand.id === brandId);
    const baseConfig = buildSelectedCtaConfig() || ctaConfig || {};
    await persistCtaConfig({
      ...baseConfig,
      brandSnapshot: selectedBrand,
      applyBranding: true,
    });
  };

  const handleSelectPreset = async (presetId: string) => {
    setSelectedPresetId(presetId);
    const selectedPreset = availableCtaPresets.find((preset: any) => preset.id === presetId);
    const linkedBrand = selectedPreset?.brandProfileId
      ? availableBrandProfiles.find((brand: any) => brand.id === selectedPreset.brandProfileId)
      : availableBrandProfiles.find((brand: any) => brand.id === selectedBrandId);
    const baseConfig = buildSelectedCtaConfig() || ctaConfig || {};
    await persistCtaConfig({
      ...baseConfig,
      presetId,
      presetSnapshot: selectedPreset,
      brandSnapshot: linkedBrand || baseConfig.brandSnapshot,
    });
    if (linkedBrand?.id) {
      setSelectedBrandId(linkedBrand.id);
    }
  };

  const handleSelectPlacement = async (placement: string) => {
    setSelectedPlacement(placement);
    const baseConfig = buildSelectedCtaConfig() || ctaConfig || {};
    await persistCtaConfig({
      ...baseConfig,
      placement,
      insertAfterSceneId: placement === 'after_scene' ? (selectedSceneId || sceneOptions[0]?.id) : undefined,
    });
  };

  const handleSelectScene = async (sceneId: string) => {
    setSelectedSceneId(sceneId);
    const baseConfig = buildSelectedCtaConfig() || ctaConfig || {};
    await persistCtaConfig({
      ...baseConfig,
      placement: 'after_scene',
      insertAfterSceneId: sceneId,
    });
  };

  return (
    <div className="panel-header h-14 border-b border-border flex items-center px-4 gap-3 flex-shrink-0">
      {/* Project Info */}
      <div className="flex items-center gap-3">
        {!isIntegration && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={handleBackClick}
            data-tooltip={t.common.cancel}
            data-tooltip-side="right"
            aria-label="Back to projects"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
        )}

        {/* Unsaved Changes Dialog */}
        <UnsavedChangesDialog
          open={showUnsavedDialog}
          onOpenChange={setShowUnsavedDialog}
          onSave={handleSave}
          projectName={project?.name}
        />

        <Separator orientation="vertical" className="h-6" />

        <div className="flex flex-col -space-y-0.5">
          <h1 className="text-sm font-medium leading-none">
            {project?.name || 'Untitled Project'}
          </h1>
          <span className="text-xs text-muted-foreground font-mono">
            {project?.width}×{project?.height} • {project?.fps}fps
          </span>
        </div>
      </div>

      <div className="flex-1" />

      {/* Shortcuts Dialog */}
      <ShortcutsDialog
        open={showShortcutsDialog}
        onOpenChange={setShowShortcutsDialog}
      />

      {/* Settings Dialog */}
      <SettingsDialog
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
      />

      {/* Save & Export */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 rounded-md border border-border px-2 py-1">
          <ScissorsLineDashed className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground whitespace-nowrap">자동 무음 제거</span>
          <Switch
            checked={autoTrimVoiceoverSilence}
            onCheckedChange={(checked) => setSetting('autoTrimVoiceoverSilence', checked)}
            aria-label="자동 무음 제거"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => void handleTrimAllVoiceovers()}
        >
          <ScissorsLineDashed className="h-4 w-4" />
          전체 무음 제거
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => {
            const currentLang = useSettingsStore.getState().language;
            useSettingsStore.getState().setSetting('language', currentLang === 'ko' ? 'en' : 'ko');
          }}
          data-tooltip={t.toolbar.language}
          data-tooltip-side="left"
          aria-label="Toggle language"
        >
          <div className="flex flex-col items-center leading-none">
            <span className="text-[10px] font-black">{useSettingsStore(s => s.language).toUpperCase()}</span>
          </div>
        </Button>

        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => {
            const currentTheme = useSettingsStore.getState().theme;
            useSettingsStore.getState().setSetting('theme', currentTheme === 'light' ? 'dark' : 'light');
          }}
          data-tooltip={t.toolbar.toggleTheme}
          data-tooltip-side="left"
          aria-label="Toggle theme"
        >
          {useSettingsStore(s => s.theme) === 'light' ? (
            <Moon className="w-4 h-4" />
          ) : (
            <Sun className="w-4 h-4" />
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-8"
          onClick={() => useEditorStore.getState().toggleTimeline()}
          data-tooltip={useEditorStore(s => s.timelineOpen) ? t.toolbar.hideTimeline : t.toolbar.showTimeline}
          data-tooltip-side="bottom"
        >
          {useEditorStore(s => s.timelineOpen) ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronUp className="w-4 h-4" />
          )}
          <span className="text-xs">{useEditorStore(s => s.timelineOpen) ? t.toolbar.hideTimeline : t.toolbar.showTimeline}</span>
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setShowSettingsDialog(true)}
          data-tooltip="Settings"
          data-tooltip-side="left"
          aria-label="Settings"
        >
          <Settings className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setShowShortcutsDialog(true)}
          data-tooltip="Shortcuts"
          data-tooltip-side="left"
          aria-label="Keyboard shortcuts"
        >
          <Keyboard className="w-4 h-4" />
        </Button>
        {!isIntegration && (
          <Button variant="outline" size="sm" className="gap-2" onClick={handleSave} aria-label="Save project">
            <div className="relative">
              <Save className="w-4 h-4" />
              {isDirty && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              )}
            </div>
            {t.common.save}
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Film className="w-4 h-4" />
              Scene
              <ChevronDown className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[300px] p-3" onFocusCapture={() => requestSceneAssets()}>
            <DropdownMenuLabel className="px-0 pt-0">저장된 씬 불러오기</DropdownMenuLabel>
            <div className="pb-2 text-xs text-muted-foreground">
              현재 playhead 위치에 저장된 씬 자산을 삽입합니다.
            </div>
            <DropdownMenuSeparator />
            <div className="space-y-2 py-2">
              <Button size="sm" variant="outline" className="w-full justify-start gap-2" onClick={requestSceneAssets}>
                <RefreshCw className={`w-4 h-4 ${isLoadingSceneAssets ? 'animate-spin' : ''}`} />
                씬 목록 새로고침
              </Button>
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">삽입 위치</div>
                <select
                  value={sceneInsertPlacement}
                  onChange={(event) => setSceneInsertPlacement(event.target.value as 'start' | 'end' | 'after_scene')}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="start">맨 앞</option>
                  <option value="end">맨 뒤</option>
                  <option value="after_scene">특정 Scene 뒤</option>
                </select>
              </div>
              {sceneInsertPlacement === 'after_scene' && sceneOptions.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">기준 Scene</div>
                  <select
                    value={sceneInsertAfterId}
                    onChange={(event) => setSceneInsertAfterId(event.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {sceneOptions.map((scene) => (
                      <option key={scene.id} value={scene.id}>
                        {scene.label} 뒤
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {savedSceneAssets.length === 0 ? (
                publicSceneTemplates.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border px-3 py-4 text-xs text-muted-foreground">
                    {isLoadingSceneAssets ? '씬 목록을 불러오는 중...' : '저장된 씬과 공용 템플릿이 없습니다.'}
                  </div>
                ) : null
              ) : (
                <></>
              )}
              {(savedSceneAssets.length > 0 || publicSceneTemplates.length > 0) && (
                <div className="max-h-[320px] space-y-3 overflow-y-auto pr-1">
                  {savedSceneAssets.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground">내 씬</div>
                      {savedSceneAssets.map((scene) => (
                        <button
                          key={scene.id}
                          type="button"
                          onClick={() => insertSavedSceneAsset(scene, {
                            placement: sceneInsertPlacement,
                            anchorSceneItemId: sceneInsertPlacement === 'after_scene' ? sceneInsertAfterId : undefined,
                          })}
                          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-left transition hover:border-foreground/20 hover:bg-accent"
                        >
                          <div className="text-sm font-medium">{scene.name}</div>
                          <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                            {scene.description || scene.payload?.scene?.description || '설명 없음'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {publicSceneTemplates.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground">공용 템플릿</div>
                      {publicSceneTemplates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => importPublicTemplate(template.id)}
                          disabled={isImportingTemplate}
                          className="block w-full rounded-md border border-amber-300/50 bg-amber-50/20 px-3 py-2 text-left transition hover:border-amber-400/70 hover:bg-accent disabled:opacity-60"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-medium">{template.name}</div>
                            <div className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-500">
                              {template.usageCount || 0} use
                            </div>
                          </div>
                          <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                            {template.description || template.payload?.scene?.description || '설명 없음'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Wand2 className="w-4 h-4" />
              CTA
              {hasCtaInTimeline && <Check className="w-3.5 h-3.5 text-emerald-500" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[280px] p-3">
            <DropdownMenuLabel className="px-0 pt-0">영상 삽입 문구</DropdownMenuLabel>
            <div className="pb-2 text-xs text-muted-foreground">
              에디터 안에서 CTA를 넣고, 갱신하고, 제거합니다.
            </div>
            <DropdownMenuSeparator />
            <div className="space-y-3 py-2">
              {availableCtaPresets.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">삽입 문구 선택</div>
                  <select
                    value={selectedPresetId}
                    onChange={(event) => void handleSelectPreset(event.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">문구 선택</option>
                    {availableCtaPresets.map((preset: any) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {availableBrandProfiles.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">채널 기본 정보 선택</div>
                  <select
                    value={selectedBrandId}
                    onChange={(event) => void handleSelectBrand(event.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">채널 선택</option>
                    {availableBrandProfiles.map((brand: any) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">삽입 위치</div>
                <select
                  value={selectedPlacement}
                  onChange={(event) => void handleSelectPlacement(event.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="end">맨 마지막</option>
                  <option value="middle">중간 자동</option>
                  <option value="after_scene">특정 씬 뒤</option>
                </select>
              </div>
              {selectedPlacement === 'after_scene' && sceneOptions.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">삽입할 씬 선택</div>
                  <select
                    value={selectedSceneId}
                    onChange={(event) => void handleSelectScene(event.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {sceneOptions.map((scene) => (
                      <option key={scene.id} value={scene.id}>
                        {scene.label} 뒤
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">브랜드 적용</div>
                  <div className="text-xs text-muted-foreground">채널 색상, 핸들, 로고를 함께 씁니다.</div>
                </div>
                <Switch checked={applyBranding} onCheckedChange={handleToggleBranding} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">로고 표시</div>
                  <div className="text-xs text-muted-foreground">브랜드 적용 시 로고를 같이 넣습니다.</div>
                </div>
                <Switch checked={applyBranding && showLogo} disabled={!applyBranding} onCheckedChange={handleToggleLogo} />
              </div>
            </div>
            <DropdownMenuSeparator />
            <div className="grid gap-2 pt-2">
              <Button size="sm" className="justify-start gap-2" onClick={handleCtaUpsert}>
                <Wand2 className="w-4 h-4" />
                {hasCtaInTimeline ? 'CTA 업데이트' : 'CTA 넣기'}
              </Button>
              <Button size="sm" variant="outline" className="justify-start gap-2" onClick={handleCtaRemove}>
                CTA 제거
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="gap-2 glow-primary-sm">
              <Download className="w-4 h-4" />
              Export
              <ChevronDown className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExport} className="gap-2">
              <Video className="w-4 h-4" />
              Export Video
            </DropdownMenuItem>
            {/* <DropdownMenuItem onClick={onExportBundle} className="gap-2">
              <FolderArchive className="w-4 h-4" />
              Download Project (.zip)
            </DropdownMenuItem> */}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
});
